import { Flex, Loader, Radio, Stack, Checkbox, Text, Anchor, Center } from "@mantine/core";
import { useState, useEffect, useCallback } from "react";

import { STEPS_ENUM } from "@widgets/StepsManager/store/types";
import { stepsStore } from "@widgets/StepsManager/store/stepsStore";
import { emailStepStore } from "@/entities/StepEmail/store/emailStepStore";
import { useTranslation } from "react-i18next";
import styles from "./stepCode.module.scss";
import { connectStepStore } from "./store/connectStepStore";
import { StepConnectTransferTypeEnum } from "./StepConnectTransferTypeEnum";
import { appStore } from "@/app/store/appStore";
import { FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType } from "@/api/gen";
import { basePath } from "@/app/AppRoutes";
import { StepNavigationButtons } from "@/entities/StepNavigationButtons/StepNavigationButtons";
import { getAuthToken } from "@/api/Auth";
import dayjs from "dayjs";
import { persistStore } from "@entities/StepFolder/store/persistStore";
import { openLink } from "@shared/utils/helpers";

export const StepConnect = () => {
	const { t } = useTranslation();
	const { transferType, setTransferType, agreedToStartFromScratch, toogleAgreedToStartFromScratch, refreshProfiles } =
		connectStepStore();
	const transferRefused = persistStore((state) => state.transferRefusedList);
	const login = emailStepStore((state) => state.login);
	const { setTransferRefused } = persistStore.getState();
	const { logout, startRequest } = emailStepStore.getState();
	const [runningInLauncher, gameConfig] = appStore((state) => [state.runningInLauncher, state.gameConfig]);

	const [connectionStarted, setConnectionStarted] = useState(false);

	function setRadioTransfer(type: string) {
		setTransferType(type);
	}

	async function goToMyGames() {
		const myGamesAppCode = gameConfig.PublicWebClientConfig?.Auth?.MyGames?.GameCode;
		const clientId = gameConfig.PublicWebClientConfig?.Auth?.MyGames?.ClientId;

		if (!clientId || !myGamesAppCode) {
			throw new Error("Missing MyGames ClientId or MyGamesAppCode");
		}
		const uriParams = {
			redirect_uri: encodeURI(`${window.location.origin}${basePath}auth/api/mygames/${myGamesAppCode}`),
			state: getAuthToken(),
			client_id: clientId,
			response_type: "code",
			signup_method: "",
			hide_default_signup: "1",
			lang: "en_US"
		};

		const uriString = Object.entries(uriParams)
			.map(([key, value]) => `${key}=${value}`)
			.join("&");
		const url = `https://account.my.games/oauth2/?${uriString}`;
		openLink(url);
		setConnectionStarted(true);
	}

	const targetType = FxIdDomainModelsDBDBSocialIdentifierSocialIdentifierType.MyGamesId;

	function handlePrevButton() {
		logout();
	}

	const  handleNextButton = useCallback (()=> {
		if (login) {
			const currentDate = dayjs();
			const newDate = currentDate.add(3, "weeks");
			setTransferRefused({ till: newDate.unix() * 1000, login });
		}
		appStore.getState().setLoginFlow(false);
	},[login, setTransferRefused]);
	const [isPrivacy, setIsPrivacy] = useState(false);

	useEffect(() => {
		if(login) {
			const showTransfer = !transferRefused[login] || dayjs().isAfter(transferRefused[login].till);
			if (!showTransfer) {
				appStore.getState().setLoginFlow(false);
			}
		}
	}, [handleNextButton, login, transferRefused]);

	const checkProfileConnection = useCallback(async () => {
		const soc = await refreshProfiles();

		if (soc[targetType]) {
			startRequest();
		}
	}, [refreshProfiles, startRequest, targetType]);

	useEffect(() => {
		// if (!runningInLauncher) {
		// 	setTransferType(StepConnectTransferTypeEnum.None);
		// }
		if (connectionStarted) {
			const intervalId = setInterval(() => {
				checkProfileConnection();
			}, 2000);
			return () => clearInterval(intervalId);
		}
		return () => {};
	}, [connectionStarted, runningInLauncher, checkProfileConnection, setTransferType]);

	return (
		<div className={styles.modalWrapper}>
			<div className={styles.wrapper}>
				<Flex miw={530} gap="xl" justify="flex-end" align="center" direction="column" wrap="wrap">
					<div />
					{
						<>
							<div>{t("stepConnect.info-1")}</div>

							<div>
								<Radio.Group value={transferType} onChange={setRadioTransfer}>
									<Stack>
										<Radio
											value={StepConnectTransferTypeEnum.TransferProgress}
											label={t("stepConnect.TransferProgress")}
										/>
										{
											<Radio
												value={StepConnectTransferTypeEnum.StartFromScratch}
												label={t("stepConnect.StartFromScratch")}
											/>
										}
									</Stack>
								</Radio.Group>
							</div>
						</>
					}
					{transferType === StepConnectTransferTypeEnum.TransferProgress && (
						<Stack>
							{t("stepConnect.info-2")}
							<Center>
								<Checkbox p={"xl"}
										  checked={isPrivacy}
										  iconColor="black"
										  onChange={() => {
											  setIsPrivacy(!isPrivacy);
										  }}
								></Checkbox>{" "}
								<div className={styles.privacyTextWrapper}>
									<Text>
										{t(
											"common.ByPressingTransferYouAgreeToPP",
											"I consent to the transfer of the data described in"
										)}{" "}
										<Anchor
											onClick={() =>
												openLink("https://documentation.my.games/terms/mygames_privacy")
											}
										>
											MY.GAMES {t("common.PrivacyPolicy", "Privacy Policy")}
										</Anchor>{" "}
										{t("common.fromMyGamesToWl", "from MY.GAMES B.V. to WL Games Ltd")}
									</Text>
								</div>
							</Center>

							{connectionStarted && <Center><Loader /></Center>}
						</Stack>
					)}
					{transferType === StepConnectTransferTypeEnum.StartFromScratch && (
						<>
							<div className={styles.privacyDescription}>{t("stepConnect.info-3")}</div>
							<div className={styles.privacyTextWrapper}>
								<Checkbox
									checked={agreedToStartFromScratch}
									onChange={toogleAgreedToStartFromScratch}
									label={t("stepConnect.ppText")}
									iconColor="black"
								/>
							</div>
						</>
					)}
					{transferType === StepConnectTransferTypeEnum.None && (
						<div className={styles.makeChoice}>{t("stepConnect.makeChoice")}</div>
					)}
				</Flex>
			</div>
			<StepNavigationButtons
				backText={t("common.back")}
				backOnClick={handlePrevButton}
				backIsDisabled={false}
				nextText={
					transferType === StepConnectTransferTypeEnum.TransferProgress
						? t("common.login_with_mygames", "Login with MyGames")
						: t("common.forward")
				}
				nextOnClick={
					transferType === StepConnectTransferTypeEnum.TransferProgress ? goToMyGames : handleNextButton
				}
				nextIsDisabled={
					transferType === StepConnectTransferTypeEnum.TransferProgress ? !isPrivacy : !agreedToStartFromScratch
				}
			/>
		</div>
	);
};
